/**
 * The Résumé line (« Forme : bonnet de 56 cm de tour, 22 cm de haut », « chaussette, jambe de
 * 17,9 cm de tour (24,4 cm au plus large), 47 cm de long », « chaussette … de long »,
 * « écharpe de 11,5 cm de large, longueur au choix », « Empiècement : encolure 44 cm, … »), from the
 * engine's own per-size numbers. Kept apart from the drawing code, which is loaded with the « Forme »
 * section.
 */
import type { CompileResult, ShapeSize } from '../../engine/types';
import { formatNumber, type Lang, type MessageKey, type Translate } from '../../i18n';

export interface ShapeLine {
  kind: ShapeSize['kind'];
  round: boolean;
  width: [number, number];
  /** Null when the length is left to the knitter. */
  height: [number, number] | null;
  /** Said "long" rather than "high" (sock, scarf, shawl, blanket, sleeve). */
  long: boolean;
  /** The final width is not read: `width` is a lower bound. */
  widthOpen: boolean;
  /** The pattern leaves the length to the knitter. */
  lengthOpen: boolean;
  /** A top-down sweater from the neck: neck, widest (body and sleeves), body below the sleeves. */
  yoke: { neck: [number, number]; widest: [number, number]; body: [number, number] | null } | null;
  /**
   * The width compared with the wearer, named (a hat's brim, a sock's leg, a mitten's hand), when the
   * engine gives it for every size; `widest` too when it differs from it by more than 1 cm (a gusset,
   * a thumb).
   */
  fit: { part: FitPart; around: [number, number]; widest: [number, number] | null } | null;
}

/** The part of the piece its fit width is measured on. */
export type FitPart = 'brim' | 'leg' | 'hand';

const FIT_PART: Partial<Record<ShapeSize['kind'], FitPart>> = { hat: 'brim', sock: 'leg', mitten: 'hand', glove: 'hand' };

/** The widest is said beside the fit width when they differ by more than this (cm). */
export const WIDEST_APART = 1;

/** Pieces whose height is a length (« de long »). */
const LONG_KINDS: ReadonlyArray<ShapeSize['kind']> = ['sock', 'scarf', 'shawl', 'blanket', 'sleeve'];

export function isLongKind(kind: ShapeSize['kind']): boolean {
  return LONG_KINDS.includes(kind);
}

function widest(s: ShapeSize): number | null {
  const widths = s.rows.map((r) => r.width_cm).filter((w): w is number => typeof w === 'number' && w > 0);
  if (s.max_width_cm !== null && s.max_width_cm > 0) return s.max_width_cm;
  return widths.length ? Math.max(...widths) : null;
}

function height(s: ShapeSize): number | null {
  if (s.total_height_cm !== null && s.total_height_cm > 0) return s.total_height_cm;
  const last = s.rows[s.rows.length - 1];
  return last && last.height_cm > 0 ? last.height_cm : null;
}

/** The neck of a yoke: the first row with a width (the cast-on). */
function firstWidth(s: ShapeSize): number | null {
  return s.rows.find((r) => typeof r.width_cm === 'number' && r.width_cm > 0)?.width_cm ?? null;
}

function span(values: number[]): [number, number] {
  return [Math.min(...values), Math.max(...values)];
}

export function shapeLine(result: CompileResult, selected: number | null): ShapeLine | null {
  const shape = result.shape;
  if (!shape) return null;
  const drawn = shape.per_size.filter(
    (s) => s.available && widest(s) !== null && (height(s) !== null || s.length_open) && (selected === null || s.size === selected),
  );
  if (drawn.length === 0) return null;
  const kind = drawn[0].kind;
  const lengthOpen = drawn.some((s) => Boolean(s.length_open));
  const heights = drawn.map(height).filter((h): h is number => h !== null);
  const widths = span(drawn.map((s) => widest(s) as number));
  // A yoke: its neck (the cast-on) and its body below the sleeves (the engine's fit width).
  const necks = kind === 'yoke' ? drawn.map(firstWidth).filter((v): v is number => v !== null) : [];
  const bodies = drawn.map((s) => s.fit_width_cm).filter((v): v is number => typeof v === 'number' && v > 0);
  const yoke = necks.length ? { neck: span(necks), widest: widths, body: bodies.length === drawn.length ? span(bodies) : null } : null;
  // A hat, a sock, a mitten: led by its fit width, named (older engines without it: the widest).
  const part = FIT_PART[kind];
  const apart = drawn.some((s) => Math.abs((widest(s) as number) - (s.fit_width_cm ?? 0)) > WIDEST_APART);
  const fit = part && bodies.length === drawn.length ? { part, around: span(bodies), widest: apart ? widths : null } : null;
  return {
    kind,
    round: (drawn[0].construction ?? result.document.construction) === 'round',
    width: widths,
    height: lengthOpen || heights.length === 0 ? null : span(heights),
    long: isLongKind(kind),
    widthOpen: drawn.some((s) => Boolean(s.width_open)),
    lengthOpen,
    yoke,
    fit,
  };
}

/** The Résumé line's text (without its « Forme : » label for a yoke, which names itself). */
export function shapeLineText(line: ShapeLine, kindName: string, t: Translate, lang: Lang): { text: string; labelled: boolean } {
  const range = ([a, b]: [number, number]) =>
    b - a < 0.05 ? formatNumber(lang, b, 1) : t('shape.range', { min: formatNumber(lang, a, 1), max: formatNumber(lang, b, 1) });
  if (line.yoke) {
    const vars = { neck: range(line.yoke.neck), widest: range(line.yoke.widest), body: line.yoke.body ? range(line.yoke.body) : '' };
    return { text: t(line.yoke.body ? 'shape.line.yoke' : 'shape.line.yokeNoBody', vars), labelled: true };
  }
  const vars = { kind: kindName, around: range(line.width), width: range(line.width), height: line.height ? range(line.height) : '' };
  let key: MessageKey;
  if (line.fit && !line.widthOpen) {
    const fitVars = {
      ...vars,
      part: t(`shape.part.${line.fit.part}`),
      around: range(line.fit.around),
      widest: line.fit.widest ? t('shape.line.fitWidest', { v: range(line.fit.widest) }) : '',
    };
    key = line.height === null ? 'shape.line.fitOpen' : line.long ? 'shape.line.fitLong' : 'shape.line.fit';
    return { text: t(key, fitVars), labelled: false };
  }
  if (line.widthOpen) key = line.height === null ? 'shape.line.bothOpen' : line.long ? 'shape.line.widthOpenLong' : 'shape.line.widthOpen';
  else if (line.height === null) key = line.round ? 'shape.line.lengthOpenRound' : 'shape.line.lengthOpen';
  else if (line.round) key = line.long ? 'shape.line.roundLong' : 'shape.line.round';
  else key = line.long ? 'shape.line.flatLong' : 'shape.line.flat';
  return { text: t(key, vars), labelled: false };
}

/**
 * The word for the piece: in French a `mitten` is a « mitaine » (most such patterns are fingerless
 * mitts), a « moufle » only when the pattern says so.
 */
export function kindKey(kind: ShapeSize['kind'], result: CompileResult): MessageKey {
  if (kind === 'mitten' && result.lines.some((l) => /moufle/i.test(l.text))) return 'shape.kind.moufle';
  return `shape.kind.${kind}`;
}
